import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { 
    GET_MAIN_CATEGORIES, 
    GET_SUB_CATEGORIES,
    CREATE_SUB_CATEGORY,
    UPDATE_SUB_CATEGORY,
    DELETE_SUB_CATEGORY,
    UPDATE_SUB_CATEGORY_ORDERS
} from '../../apollo/queries';
import '../css/CategoryRankingPage.css';

// DnD Kit imports
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable SubCategory Item Component
const SortableSubCategoryItem = ({ subCategory, index, onSelect, isSelected, onEdit, onDelete }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: subCategory.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`crp-list-box-item ${isSelected ? 'selected' : ''}`}
            onClick={() => onSelect(subCategory)}
            {...attributes}
        >
            <div className="crp-drag-handle" {...listeners}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 6h8v2H8V6zm0 4h8v2H8v-2zm0 4h8v2H8v-2z" fill="#666"/>
                </svg>
            </div>
            <span className="crp-category-name">{subCategory.name}</span>
            <div className="crp-item-actions">
                <button 
                    className="crp-edit-btn" 
                    onClick={(e) => {
                        e.stopPropagation();
                        onEdit(subCategory);
                    }}
                >
                    수정
                </button>
                <button 
                    className="crp-delete-btn" 
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete(subCategory);
                    }}
                >
                    삭제
                </button>
            </div>
        </div>
    );
};

const CategoryRankingPage = () => {
    const navigate = useNavigate();
    const [selectedMainCategoryId, setSelectedMainCategoryId] = useState('');
    const [selectedSubCategory, setSelectedSubCategory] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    
    // 폼 상태
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        bannerImage: '',
        bannerUrl: '',
        isActive: true
    });

    // DnD 센서 설정
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // GraphQL 쿼리
    const { data: mainCategoriesData, loading: mainCategoriesLoading } = useQuery(GET_MAIN_CATEGORIES, {
        errorPolicy: 'all'
    });

    const { data: subCategoriesData, loading: subCategoriesLoading, refetch: refetchSubCategories } = useQuery(GET_SUB_CATEGORIES, {
        variables: { mainCategoryId: selectedMainCategoryId || null },
        skip: !selectedMainCategoryId,
        errorPolicy: 'all'
    });

    // 뮤테이션들
    const [createSubCategory] = useMutation(CREATE_SUB_CATEGORY, {
        onCompleted: () => {
            refetchSubCategories();
            resetForm();
            alert('하위 카테고리가 성공적으로 생성되었습니다.');
        },
        onError: (error) => {
            alert(`하위 카테고리 생성 실패: ${error.message}`);
        }
    });

    const [updateSubCategory] = useMutation(UPDATE_SUB_CATEGORY, {
        onCompleted: () => {
            refetchSubCategories();
            resetForm();
            alert('하위 카테고리가 성공적으로 수정되었습니다.');
        },
        onError: (error) => {
            alert(`하위 카테고리 수정 실패: ${error.message}`);
        }
    });

    const [deleteSubCategory] = useMutation(DELETE_SUB_CATEGORY, {
        onCompleted: () => {
            refetchSubCategories();
            resetForm();
            alert('하위 카테고리가 성공적으로 삭제되었습니다.');
        },
        onError: (error) => {
            alert(`하위 카테고리 삭제 실패: ${error.message}`);
        }
    });

    const [updateSubCategoryOrders] = useMutation(UPDATE_SUB_CATEGORY_ORDERS, {
        onCompleted: () => {
            refetchSubCategories();
        },
        onError: (error) => {
            alert(`순서 변경 실패: ${error.message}`);
        }
    });

    const mainCategories = mainCategoriesData?.mainCategories || [];
    const subCategories = subCategoriesData?.subCategories || [];

    // 드래그 앤 드롭 핸들러
    const handleDragEnd = (event) => {
        const { active, over } = event;

        if (active.id !== over?.id) {
            const oldIndex = subCategories.findIndex(cat => cat.id === active.id);
            const newIndex = subCategories.findIndex(cat => cat.id === over.id);

            const reorderedSubCategories = arrayMove(subCategories, oldIndex, newIndex);
            const categoryOrders = reorderedSubCategories.map((cat, index) => ({
                id: cat.id,
                order: index
            }));

            updateSubCategoryOrders({
                variables: { categoryOrders }
            });
        }
    };

    // 메인 카테고리 선택 핸들러
    const handleMainCategoryChange = (e) => {
        const categoryId = e.target.value;
        setSelectedMainCategoryId(categoryId);
        setSelectedSubCategory(null);
        resetForm();
    };

    // 하위 카테고리 선택 핸들러
    const handleSubCategorySelect = (subCategory) => {
        setSelectedSubCategory(subCategory);
        setFormData({
            name: subCategory.name,
            description: subCategory.description || '',
            bannerImage: subCategory.bannerImage || '',
            bannerUrl: subCategory.bannerUrl || '',
            isActive: subCategory.isActive
        });
        setIsEditing(false);
        setIsCreating(false);
    };

    // 편집 모드 활성화
    const handleEdit = (subCategory) => {
        setSelectedSubCategory(subCategory);
        setFormData({
            name: subCategory.name,
            description: subCategory.description || '',
            bannerImage: subCategory.bannerImage || '',
            bannerUrl: subCategory.bannerUrl || '',
            isActive: subCategory.isActive
        });
        setIsEditing(true);
        setIsCreating(false);
    };

    // 새 카테고리 생성 모드
    const handleCreate = () => {
        if (!selectedMainCategoryId) {
            alert('먼저 메인 카테고리를 선택해주세요.');
            return;
        }
        resetForm();
        setIsCreating(true);
        setIsEditing(false);
        setSelectedSubCategory(null);
    };

    // 삭제 핸들러
    const handleDelete = (subCategory) => {
        if (window.confirm(`정말로 '${subCategory.name}' 카테고리를 삭제하시겠습니까?`)) {
            deleteSubCategory({
                variables: { id: subCategory.id }
            });
        }
    };

    // 폼 제출 핸들러
    const handleSubmit = () => {
        if (!formData.name.trim()) {
            alert('카테고리명을 입력해주세요.');
            return;
        }

        if (!selectedMainCategoryId) {
            alert('메인 카테고리를 선택해주세요.');
            return;
        }

        const input = {
            name: formData.name.trim(),
            description: formData.description.trim() || null,
            bannerImage: formData.bannerImage.trim() || null,
            bannerUrl: formData.bannerUrl.trim() || null,
            order: subCategories.length,
            isActive: formData.isActive,
            mainCategoryId: selectedMainCategoryId
        };

        if (isCreating) {
            createSubCategory({
                variables: { input }
            });
        } else if (isEditing && selectedSubCategory) {
            updateSubCategory({
                variables: { 
                    id: selectedSubCategory.id, 
                    input: {
                        ...input,
                        order: selectedSubCategory.order // 기존 순서 유지
                    }
                }
            });
        }
    };

    // 폼 리셋
    const resetForm = () => {
        setFormData({
            name: '',
            description: '',
            bannerImage: '',
            bannerUrl: '',
            isActive: true
        });
        setIsEditing(false);
        setIsCreating(false);
        setSelectedSubCategory(null);
    };

    // 폼 입력 핸들러
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    if (mainCategoriesLoading) {
        return <div>로딩 중...</div>;
    }

    return (
        <>
            <div className="breadcrumb">
                <span>홈 &gt; 카테고리 관리 &gt; 카테고리 순위</span>
            </div>
            <div className="admin-main-content">
                <aside className="admin-sidebar">
                    <div className="sidebar-menu">
                        <h3>카테고리 관리</h3>
                        <ul>
                            <li onClick={() => navigate('/admin/category')}>카테고리 관리</li>
                            <li className="active">카테고리 순위</li>
                        </ul>
                    </div>
                </aside>
                <main className="admin-content-ranking">
                    <div className="crp-page-header">
                        <h2>카테고리 순위 관리</h2>
                        <p>하위 카테고리의 순서를 드래그 앤 드롭으로 변경하고 관리할 수 있습니다.</p>
                    </div>

                    <div className="crp-category-ranking-container">
                        <div className="crp-form-row">
                            <label>메인 카테고리 선택</label>
                            <select 
                                value={selectedMainCategoryId} 
                                onChange={handleMainCategoryChange}
                                className="crp-main-category-select"
                            >
                                <option value="">메인 카테고리를 선택하세요</option>
                                {mainCategories.map(category => (
                                    <option key={category.id} value={category.id}>
                                        {category.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {selectedMainCategoryId && (
                            <div className="crp-ranking-interface">
                                <div className="crp-category-list-section">
                                    <div className="crp-list-header">
                                        <h3>하위 카테고리 목록</h3>
                                        <button 
                                            className="crp-btn-create" 
                                            onClick={handleCreate}
                                        >
                                            + 새 카테고리 추가
                                        </button>
                                    </div>
                                    <div className="crp-category-list-box">
                                        {subCategoriesLoading ? (
                                            <div className="crp-loading">로딩 중...</div>
                                        ) : subCategories.length === 0 ? (
                                            <div className="crp-empty">등록된 하위 카테고리가 없습니다.</div>
                                        ) : (
                                            <DndContext
                                                sensors={sensors}
                                                collisionDetection={closestCenter}
                                                onDragEnd={handleDragEnd}
                                            >
                                                <SortableContext
                                                    items={subCategories.map(cat => cat.id)}
                                                    strategy={verticalListSortingStrategy}
                                                >
                                                    {subCategories.map((subCategory, index) => (
                                                        <SortableSubCategoryItem
                                                            key={subCategory.id}
                                                            subCategory={subCategory}
                                                            index={index}
                                                            onSelect={handleSubCategorySelect}
                                                            isSelected={selectedSubCategory?.id === subCategory.id}
                                                            onEdit={handleEdit}
                                                            onDelete={handleDelete}
                                                        />
                                                    ))}
                                                </SortableContext>
                                            </DndContext>
                                        )}
                                    </div>
                                </div>

                                <div className="crp-category-details">
                                    <h3>
                                        {isCreating ? '새 카테고리 추가' : 
                                         isEditing ? '카테고리 수정' : 
                                         '카테고리 정보'}
                                    </h3>
                                    
                                    <div className="crp-form-group">
                                        <label>카테고리명</label>
                                        <input 
                                            type="text" 
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            placeholder="카테고리명을 입력하세요"
                                            disabled={!isEditing && !isCreating}
                                        />
                                    </div>
                                    
                                    <div className="crp-form-group">
                                        <label>설명</label>
                                        <textarea 
                                            name="description"
                                            value={formData.description}
                                            onChange={handleInputChange}
                                            placeholder="카테고리 설명을 입력하세요"
                                            disabled={!isEditing && !isCreating}
                                            rows="3"
                                        />
                                    </div>
                                    
                                    <div className="crp-form-group">
                                        <label>배너 이미지 URL</label>
                                        <input 
                                            type="text" 
                                            name="bannerImage"
                                            value={formData.bannerImage}
                                            onChange={handleInputChange}
                                            placeholder="배너 이미지 URL을 입력하세요"
                                            disabled={!isEditing && !isCreating}
                                        />
                                    </div>
                                    
                                    <div className="crp-form-group">
                                        <label>배너 링크 URL</label>
                                        <input 
                                            type="text" 
                                            name="bannerUrl"
                                            value={formData.bannerUrl}
                                            onChange={handleInputChange}
                                            placeholder="배너 링크 URL을 입력하세요"
                                            disabled={!isEditing && !isCreating}
                                        />
                                    </div>
                                    
                                    <div className="crp-form-group-checkbox">
                                        <label>
                                            <input 
                                                type="checkbox" 
                                                name="isActive"
                                                checked={formData.isActive}
                                                onChange={handleInputChange}
                                                disabled={!isEditing && !isCreating}
                                            /> 
                                            공개
                                        </label>
                                    </div>

                                    {(isEditing || isCreating) && (
                                        <div className="crp-form-actions">
                                            <button 
                                                className="crp-btn-cancel" 
                                                onClick={resetForm}
                                            >
                                                취소
                                            </button>
                                            <button 
                                                className="crp-btn-save" 
                                                onClick={handleSubmit}
                                            >
                                                {isCreating ? '생성' : '저장'}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="crp-bottom-actions">
                        <button className="crp-btn-back" onClick={() => navigate('/admin/category')}>
                            &lt; 카테고리 관리로 돌아가기
                        </button>
                    </div>
                </main>
            </div>
        </>
    );
};

export default CategoryRankingPage; 